/**
 * Credential Field Definitions
 * 
 * Defines the credential fields required for each data source
 */

import { DataSourceType } from '../services/dataSourceRegistry';
import { CredentialField } from '../components/CredentialManagementModal';

export function getCredentialFields(source: DataSourceType): CredentialField[] {
  switch (source) {
    case 'samgov':
      return [
        {
          id: 'api_key',
          label: 'API Key',
          type: 'api_key',
          required: true,
          placeholder: 'Enter SAM.gov API key',
          description: 'Get your API key from https://open.gsa.gov/api/',
        },
      ];
    
    case 'bc_bid':
      return [
        {
          id: 'username',
          label: 'Username',
          type: 'text',
          required: true,
          placeholder: 'Enter BC Bid username',
        },
        {
          id: 'password',
          label: 'Password',
          type: 'password',
          required: true,
          placeholder: 'Enter BC Bid password',
        },
        {
          id: 'api_key',
          label: 'API Key (if available)',
          type: 'api_key',
          required: false,
          placeholder: 'Enter API key if available',
        },
      ];
    
    case 'bids_tender':
      return [
        {
          id: 'username',
          label: 'Username',
          type: 'text',
          required: true,
          placeholder: 'rrai@keystoneenvironmental.ca',
          description: 'Default: rrai@keystoneenvironmental.ca',
        },
        {
          id: 'password',
          label: 'Password',
          type: 'password',
          required: true,
          placeholder: 'Enter Bids&Tender password',
        },
        {
          id: 'api_key',
          label: 'API Key (if available)',
          type: 'api_key',
          required: false,
          placeholder: 'Enter API key if available',
        },
      ];
    
    case 'bonfire':
      return [
        {
          id: 'username',
          label: 'Username',
          type: 'text',
          required: true,
          placeholder: 'aboons@keystoneenvironmental.ca',
          description: 'Default: aboons@keystoneenvironmental.ca',
        },
        {
          id: 'password',
          label: 'Password',
          type: 'password',
          required: true,
          placeholder: 'Enter Bonfire password',
        },
        {
          id: 'api_key',
          label: 'API Key',
          type: 'api_key',
          required: true,
          placeholder: 'Enter Bonfire API key',
          description: 'Required for API access',
        },
      ];
    
    case 'gbof':
      return [
        {
          id: 'username',
          label: 'Email',
          type: 'text',
          required: true,
          placeholder: 'your@email.com',
          description: 'GBOF login email (GBOF_EMAIL)',
        },
        {
          id: 'password',
          label: 'Password',
          type: 'password',
          required: true,
          placeholder: 'Enter GBOF password',
          description: 'GBOF login password (GBOF_PASSWORD)',
        },
      ];
    
    case 'merx':
      return [
        {
          id: 'username',
          label: 'Username',
          type: 'text',
          required: true,
          placeholder: 'wdonald',
          description: 'Default: wdonald',
        },
        {
          id: 'password',
          label: 'Password',
          type: 'password',
          required: true,
          placeholder: 'Enter Merx password',
        },
        {
          id: 'api_key',
          label: 'API Key (if available)',
          type: 'api_key',
          required: false,
          placeholder: 'Enter API key if available',
        },
      ];
    
    case 'sap_ariba':
      return [
        {
          id: 'cookies',
          label: 'Session Cookies',
          type: 'textarea',
          required: true,
          placeholder: 'Paste session cookies from browser (rxVisitor, SESSION, etc.)',
          description: 'Copy cookies from browser DevTools when logged into portal.us.bn.cloud.ariba.com',
        },
        {
          id: 'csrf_token',
          label: 'CSRF Token',
          type: 'text',
          required: true,
          placeholder: 'Enter CSRF token (x-csrf-token header value)',
          description: 'Found in browser DevTools Network tab as x-csrf-token header',
        },
        {
          id: 'username',
          label: 'Username (Optional)',
          type: 'text',
          required: false,
          placeholder: 'Enter SAP Ariba username (for legacy API)',
          description: 'Only needed for legacy OAuth API, not Discovery 2.0',
        },
        {
          id: 'password',
          label: 'Password',
          type: 'password',
          required: true,
          placeholder: 'Enter SAP Ariba password',
        },
        {
          id: 'api_key',
          label: 'API Key',
          type: 'api_key',
          required: true,
          placeholder: 'Enter SAP Ariba API key',
        },
        {
          id: 'tenant_id',
          label: 'Tenant ID',
          type: 'text',
          required: false,
          placeholder: 'Enter tenant ID if required',
        },
      ];
    
    case 'jaggaer':
      return [
        {
          id: 'username',
          label: 'Username',
          type: 'text',
          required: true,
          placeholder: 'Enter Jaggaer username',
          description: 'Multiple accounts may be supported',
        },
        {
          id: 'password',
          label: 'Password',
          type: 'password',
          required: true,
          placeholder: 'Enter Jaggaer password',
        },
        {
          id: 'api_key',
          label: 'API Key',
          type: 'api_key',
          required: true,
          placeholder: 'Enter Jaggaer API key',
        },
        {
          id: 'tenant_id',
          label: 'Tenant ID',
          type: 'text',
          required: false,
          placeholder: 'Enter tenant ID if required',
        },
      ];
    
    case 'city_portals':
      return [
        {
          id: 'username',
          label: 'Username (if required)',
          type: 'text',
          required: false,
          placeholder: 'Enter city portal username if required',
          description: 'Most city portals are public, but some may require login',
        },
        {
          id: 'password',
          label: 'Password (if required)',
          type: 'password',
          required: false,
          placeholder: 'Enter city portal password if required',
        },
      ];
    
    case 'vancouver_city_portal':
      return [
        {
          id: 'cookies',
          label: 'Session Cookies',
          type: 'textarea',
          required: true,
          placeholder: 'Paste all session cookies from browser',
          description: 'Required for authentication with Vancouver Jaggaer portal. Copy from browser developer tools.',
        },
        {
          id: 'username',
          label: 'Username (Optional)',
          type: 'text',
          required: false,
          placeholder: 'Enter Vancouver portal username',
          description: 'Optional - may be needed for initial login to get cookies',
        },
        {
          id: 'password',
          label: 'Password (Optional)',
          type: 'password',
          required: false,
          placeholder: 'Enter Vancouver portal password',
          description: 'Optional - may be needed for initial login to get cookies',
        },
      ];
    
    case 'victoria_city_portal':
      return [
        {
          id: 'cookies',
          label: 'Session Cookies',
          type: 'textarea',
          required: true,
          placeholder: 'Paste all session cookies from browser',
          description: 'Required for authentication with Victoria Bonfire portal. Copy from browser developer tools.',
        },
        {
          id: 'username',
          label: 'Username (Optional)',
          type: 'text',
          required: false,
          placeholder: 'Enter Victoria portal username',
          description: 'Optional - may be needed for initial login to get cookies',
        },
        {
          id: 'password',
          label: 'Password (Optional)',
          type: 'password',
          required: false,
          placeholder: 'Enter Victoria portal password',
          description: 'Optional - may be needed for initial login to get cookies',
        },
      ];
    
    case 'metrovancouver_city_portal':
      return [
        {
          id: 'cookies',
          label: 'Session Cookies',
          type: 'textarea',
          required: true,
          placeholder: 'Paste all session cookies from browser (including __RequestVerificationToken)',
          description: 'Required for authentication with Metro Vancouver BidsAndTenders portal. Copy from browser developer tools. Must include __RequestVerificationToken cookie.',
        },
        {
          id: 'csrf_token',
          label: 'CSRF Token (Optional)',
          type: 'text',
          required: false,
          placeholder: 'Enter CSRF token if not in cookies',
          description: 'Optional - CSRF token should be in cookies as __RequestVerificationToken, but can be provided separately if needed',
        },
        {
          id: 'username',
          label: 'Username (Optional)',
          type: 'text',
          required: false,
          placeholder: 'Enter Metro Vancouver portal username',
          description: 'Optional - may be needed for initial login to get cookies',
        },
        {
          id: 'password',
          label: 'Password (Optional)',
          type: 'password',
          required: false,
          placeholder: 'Enter Metro Vancouver portal password',
          description: 'Optional - may be needed for initial login to get cookies',
        },
      ];
    
    default:
      return [];
  }
}

/**
 * Check if a data source requires credentials
 */
export function requiresCredentials(source: DataSourceType): boolean {
  const fields = getCredentialFields(source);
  return fields.length > 0;
}


